import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs'
import clsx from 'clsx'
import { forwardRef, useCallback, useRef, type MutableRefObject } from 'react'
import { ComponentRenderer } from '../../AppPage2/ContentPanel/TileGrid/ComponentRenderer'
import { TComponentConfig } from '../../AppPage2/types'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { TabContainerDataSchema, TabContainerMetaData, TabContainerUiSchema } from './types'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'
import { ScrollArea, ScrollBar } from '@/components/shadcn/scroll-area'
import { IconBg } from '@/components/ui/IconBg'
import { SnapScrollPanel } from '../shared/SnapScrollPanel'

export function TabContainerContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: TabContainerUiSchema
  dataSchema: TabContainerDataSchema
  tileFilters?: React.ReactNode
}) {
  const { title = undefined, description = undefined, layout, defaultSelectedTab, metaData, emphasizeHeader } = uiSchema
  const { tabData } = dataSchema
  const isVertical = layout === 'vertical'
  const hasHeader = !!title && !!tileFilters
  const hasDescriptions = tabData.some((tab) => !!tab.description)
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      <div className={clsx({ 'pt-5': hasHeader })} style={{ height: contentHeight }}>
        <Tabs
          className="h-full w-full data-[orientation=vertical]:flex data-[orientation=vertical]:gap-x-5 data-[orientation=horizontal]:space-y-5"
          defaultValue={defaultSelectedTab}
          orientation={layout}
        >
          <div className={clsx('flex', { 'h-full w-52': isVertical, 'w-full': !isVertical })}>
            <ScrollArea className={clsx('w-full', { 'h-full': isVertical })}>
              <TabsList
                // className={clsx('h-auto shadow', {
                className={clsx('h-auto rounded-none bg-offwhite', {
                  'flex w-52 flex-col justify-start': isVertical,
                  'w-max': !isVertical,
                })}
              >
                {tabData.map((tab) => (
                  <TabsTrigger
                    className={clsx(
                      // 'text-normal group flex-col justify-start data-[orientation=horizontal]:h-full data-[state=active]:bg-background data-[state=active]:text-primary',
                      'text-normal group flex-col justify-start data-[orientation=horizontal]:h-full data-[state=active]:bg-background dark:data-[state=active]:bg-accent dark:data-[state=active]:text-accent-foreground',
                      {
                        'w-full items-start': isVertical,
                      }
                    )}
                    key={tab.id}
                    value={tab.id}
                  >
                    <div className="inline-flex w-full items-end justify-between gap-x-2">
                      <span className="max-w-[184px] truncate text-base group-data-[state=active]:font-bold">
                        {tab.title}
                      </span>
                      <StateIcon state={tab.state} metaData={metaData} />
                    </div>
                    {tab.description ? (
                      <TooltipButton tooltip={tab.description}>
                        <span className="inline-block max-w-[184px] truncate text-xs font-light text-muted-foreground">
                          {tab.description}
                        </span>
                      </TooltipButton>
                    ) : (
                      <span className="inline-block max-w-[184px] truncate text-xs font-light text-muted-foreground">
                        {tab.description}
                      </span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
          <div
            className={clsx({ 'max-w-[calc(100%-13rem-20px)] flex-1': isVertical })}
            style={{
              height: isVertical ? '100%' : `calc(100% - ${hasDescriptions ? '80px' : '66px'})`,
            }}
          >
            {tabData.map((tab) => {
              const hasMultipleComponents = tab.components.length > 1
              return (
                <TabsContent className="mt-0 h-full w-full" key={tab.id} value={tab.id}>
                  {hasMultipleComponents ? (
                    <SnapScrollPanel components={tab.components} containerId={tab.id} />
                  ) : (
                    tab.components.map((component) => (
                      <TabComponent key={component.id} component={component} containerId={tab.id} />
                    ))
                  )}
                </TabsContent>
              )
            })}
          </div>
        </Tabs>
      </div>
    </div>
  )
}

export const TabComponent = forwardRef<
  HTMLDivElement,
  {
    component: TComponentConfig
    containerId: string
    sectionIndex?: number
    snapScroll?: boolean
  }
>(function TabComponent({ component, containerId, sectionIndex, snapScroll }, forwardedRef) {
  const tileContainerRef = useRef<HTMLDivElement | null>(null) as MutableRefObject<HTMLDivElement | null>

  const setContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      tileContainerRef.current = element
      if (typeof forwardedRef === 'function') {
        forwardedRef(element)
      } else if (forwardedRef) {
        ;(forwardedRef as MutableRefObject<HTMLDivElement | null>).current = element
      }
    },
    [forwardedRef]
  )

  return (
    <div
      className={clsx('w-full', snapScroll ? 'h-full shrink-0 snap-start snap-always overflow-hidden' : 'h-full')}
      data-section-index={sectionIndex}
      ref={setContainerRef}
    >
      <div className="flex size-full flex-col gap-y-5 [&>div]:p-0">
        <ComponentRenderer component={component} containerId={containerId} tileContainerRef={tileContainerRef} />
      </div>
    </div>
  )
})

const ICON_VARIANT_MAP = {
  grey: 'outline',
  yellow: 'warning',
  green: 'success',
  red: 'error',
} as const

function StateIcon({ state, metaData }: { state: string; metaData?: TabContainerMetaData[] }) {
  if (!metaData) return null
  const property = metaData.find((item) => item.propertyKey === 'state')
  if (!property) return null
  const mapping = property.mapping.find((item) => item.value === state)
  if (!mapping?.icon || state === 'None') return null
  const variant = ICON_VARIANT_MAP[mapping.color as keyof typeof ICON_VARIANT_MAP]
  return (
    <IconBg className="size-7" tooltip={state} variant={variant}>
      <DynamicIcon iconName={mapping.icon} className="size-4" />
      <span className="sr-only">{state}</span>
    </IconBg>
  )
}
